import React from 'react';

import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import CaseStudyNextSteps from '../components/CaseStudyNextSteps';
import PageHero from '../components/stitch/PageHero';
import { cardSx } from '../components/stitch/Section';
import { caseStudies, caseStudyPath } from '../data/CaseStudies';
import { tokens } from '../theme';

// Built to the Stitch "Case Studies" screen (project 1587770953174176333), with the site's own copy.
export const attributionPillSx = {
  display: 'inline-block',
  px: 1.5,
  py: 0.5,
  borderRadius: 1,
  bgcolor: tokens.surfaceContainer,
  color: tokens.primary,
  overflowWrap: 'anywhere',
} as const;

export const textLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  color: tokens.accentText,
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: 20 / 14,
  textUnderlineOffset: '0.25em',
} as const;

function CaseStudies() {
  return (
    <Container>
      <PageHero
        eyebrow='CMS case studies'
        title='CMS work in practice'
        intro='These delivered projects show how publishing tools served different teams. Each example identifies the work and its attribution.'
      />

      <Box
        component='nav'
        aria-label='Case study index'
        sx={{ ...cardSx, p: { xs: 3, md: 4 }, maxWidth: '56rem' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountTreeOutlinedIcon
            aria-hidden='true'
            sx={{ fontSize: 20, color: tokens.accentText }}
          />
          <Typography
            component='h2'
            variant='h5'
          >
            Explore the case studies
          </Typography>
        </Box>
        <Box
          component='ol'
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {caseStudies.map((study, index) => (
            <Box
              component='li'
              key={study.id}
              sx={{
                minWidth: 0,
                gridColumn: { md: index === caseStudies.length - 1 ? '1 / -1' : 'auto' },
              }}
            >
              <Box
                component={Link}
                to={caseStudyPath(study.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: tokens.surfaceLow,
                  color: tokens.primary,
                  fontSize: '0.875rem',
                  lineHeight: 20 / 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { bgcolor: tokens.surfaceContainer, textDecoration: 'underline' },
                }}
              >
                <Box
                  component='span'
                  sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                >
                  <span aria-hidden='true'>{index + 1}. </span>
                  {study.title}
                </Box>
                <ArrowForwardIcon
                  aria-hidden='true'
                  sx={{ fontSize: 18, color: tokens.outline, flexShrink: 0 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: { xs: 5, md: 8 }, display: 'grid', gap: { xs: 3, md: 4 } }}>
        {caseStudies.map((study) => (
          <Box
            component='article'
            key={study.id}
            id={study.id}
            tabIndex={-1}
            aria-labelledby={`${study.id}-title`}
            sx={{ ...cardSx, minWidth: 0, p: { xs: 3, md: 5 }, scrollMarginTop: '96px' }}
          >
            <Typography
              variant='labelCaps'
              component='p'
              sx={attributionPillSx}
            >
              {study.attribution}
            </Typography>
            <Typography
              component='h2'
              id={`${study.id}-title`}
              variant='h3'
              aria-label={`${study.title} — ${study.attribution}`}
              sx={{ mt: 2.5, overflowWrap: 'anywhere' }}
            >
              {study.title}
            </Typography>
            <Box
              component='dl'
              sx={{
                m: 0,
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 3,
              }}
            >
              {(
                [
                  ['The need', study.problem],
                  ['What changed', study.result],
                ] as const
              ).map(([label, value]) => (
                <Box
                  key={label}
                  sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, bgcolor: tokens.surfaceLow }}
                >
                  <Typography
                    component='dt'
                    variant='labelCaps'
                    sx={{ display: 'block', color: tokens.accentText }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component='dd'
                    variant='body1'
                    sx={{ m: 0, mt: 1 }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box
              component={Link}
              to={caseStudyPath(study.id)}
              aria-label={`Read the case study: ${study.title}`}
              sx={{ ...textLinkSx, mt: 3 }}
            >
              Read the case study
              <ArrowForwardIcon
                aria-hidden='true'
                sx={{ fontSize: 18 }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <CaseStudyNextSteps />
    </Container>
  );
}

export default CaseStudies;
